import {
  DataTableDataItemValue,
  IDataTable,
  IElementDataTableProperty,
  IFactoryElementDataTableProperty,
} from '@shared/models/data-table.model';
import { SemiPartial } from '@shared/models/type-helpers.model';
import { validateDataTable } from '@shared/validation/data-table.validation';
import { v4 } from 'uuid';
import { getTimestamp, getTimestamps } from '../date.helpers';
import { preventChangesInKeys, replaceProperties } from '../object.helpers';
import {
  ElementPropertyType,
  IElementProperty,
  IFactorySelectProperty,
} from '@shared/models/element-property.model';
import { createElementProperties } from '../element-property-factory.helpers';
import { formatExpressionVariableName } from '../expression-variables.helpers';
import { isDataTableDataItemValue } from './data-table.helpers';
import { guards } from '../typeguards.helpers';
import { uniq } from 'lodash';
import { setDataTablePropertiesByDataItem } from './data-table-pipeline.helpers';

/**
 * Create a new data table with the default values.
 * @param defaults The default values for the data table
 * @returns The created data table
 */
export const createDataTable = (
  defaults: SemiPartial<
    IDataTable,
    'organizations' | 'owner' | 'name' | 'index_key'
  >,
): IDataTable => {
  if (!defaults.organizations.length) {
    throw new Error('Organizations are required');
  }
  return validateDataTable({
    ...getTimestamps(),
    id: v4(),
    data: [],
    properties: [],
    ...defaults,
  });
};

/**
 * Update a data table with the changes. Will also validate the data table.
 * @param existingDataTable The existing data table
 * @param changes The changes to the data table
 * @returns The updated data table
 */
export const updateDataTable = (
  existingDataTable: IDataTable,
  changes: Partial<IDataTable>,
): IDataTable => {
  // Prevent changes to forbidden keys
  preventChangesInKeys(
    existingDataTable,
    changes,
    'id',
    'created_at',
    'deleted_at',
    'organizations',
    'owner',
  );

  // Replace properties with the changes
  return validateDataTable(replaceProperties(existingDataTable, changes));
};

/**
 * Get element properties from a data table.
 * @param dataTable
 * @returns
 */
export const createElementPropertiesFromDataTable = (
  dataTable: IDataTable,
): IElementDataTableProperty[] => {
  const { index_key } = dataTable;
  const base: IFactoryElementDataTableProperty = {
    table_id: dataTable.id,
    type: ElementPropertyType.Select,
    inheritFallback: false,
  };
  const mainProperty: IElementProperty[] = createElementProperties([
    {
      ...base,
      name: index_key, // Use index_key (key in json-data) as name
      displayName: dataTable.name, // Show the data table name in the UI
      options: getDataTableValues(dataTable, index_key),
      count: dataTable.data[0]?.[index_key],
      updated_at: getTimestamp(),
    } as IFactorySelectProperty,
  ]);
  const childProperties = createElementProperties(
    dataTable.properties.map((property) => ({
      ...base,
      name: formatExpressionVariableName(property),
      options: getDataTableValues(dataTable, property),
      indentation: 1,
      count: undefined,
    })),
  ).map((p) => {
    return {
      ...p,
      count: undefined,
    };
  });

  return setDataTablePropertiesByDataItem(
    [...mainProperty, ...childProperties],
    dataTable,
  ) as IElementDataTableProperty[];
};

/**
 * Get available values for a property in the data table
 * @param dataTable
 * @param key
 * @returns
 */
const getDataTableValues = (
  dataTable: IDataTable,
  key: string,
): DataTableDataItemValue[] =>
  uniq(
    dataTable.data
      .map((row) => (isDataTableDataItemValue(row[key]) ? row[key] : undefined))
      .filter(guards.is.defined),
  );
